/*
 * Created By sswaminathan
 * Created on Sat Sep 11 2021
 * Created on TenantSwitcherPage.js
 * Copyright (c) 2021  ©Palo Alto Networks
 */


/**
 * TenantswitcherPage handles the component segregation
 * for each section of the TenantSwitcher. The reducers has the data for rendering
 * are passed as parameters to the component and data is passed to the child components.
 *
 **/
import { useCallback, useEffect, useMemo, useState, useContext } from 'react'
import { Body, Button } from '@panwds/react-ui-legacy'
import CommonServices from '../commonServices/CommonServices'
import { LABEL_AGGREGATE_SERVICES, LABEL_COMMON_SERVICES } from '../../../tenancy/constants/LabelConstants'
import TreeComponent from '../tenantTree/TreeComponent'
import TenantSearch from '../tenantSearch/TenantSearch'
import './tenant-switcher-page.scss'
import { SWITCH_MULTI_TENANT_PORTAL, HUB, ASC } from '../../../common/constants/LabelConstants'
import { ASC_PORTAL_MICRO_APP, ASC_URL, ASC_TIER_1_SUPPORT_ROLE, MSP_URL, GOV_REGION } from '../../../common/constants/GlobalConstants'
import { ROOT_NODE_ID } from '../../../tenancy/constants/TenancyConstants'
import { LoadAllInstanceData, makeTenantSwitcherLoad } from '../../../common/actions/GlobalActions'
import AppContext from '../../../common/context/AppContext'
import { keys } from 'lodash'
import { selectTreeNode, loadChildInstanceData } from '../../../common/reducers/actions/TreeReducer.actions'
import { setIsGlobalModuleOpen, getFrameworkVar, getFrameworkServiceUrl, getTsgAccess, hasMicroApp, launchMultiTenantView } from '../../../common/utils/Sparky'
import AppImage from '../../../common/components/appName/AppImage'
import { HUB_LANDING_PATH } from '../../../common/constants/GlobalConstants'
import { FF_USE_NEW_LEFT_NAV } from '../../../common/utils/FeatureFlags'

const hideCommonServices = getFrameworkVar(FF_USE_NEW_LEFT_NAV)


/**
 *
 * @param pageHeaderProps
 * @param globalDispatch
 * @param handleHeightonToggle
 * @param rest
 * @returns {JSX.Element}
 * @constructor
 */
const TenantSwitcherPage = ({
  pageHeaderProps,
  globalDispatch,
  handleHeightonToggle,
  ...rest
}) => {
  const { sgKeyBy, sgTree, dispatch, selectedTreeNode } = rest
  const [tsgAccess, setTsgAccess] = useState()
  const [isIPRestrictedError, setIPRestrictedError] = useState(false)
  rest.tsgAccess = tsgAccess

  const onBreadCrumbClick = useCallback((obj) => {
    dispatch(selectTreeNode(obj))
    if (obj?.id !== ROOT_NODE_ID) {
      makeTenantSwitcherLoad(dispatch, true)

      async function fetchtokens() {
        try {
          if (!rest.instancesKeyBy[keys]) {
            const keys = new Array(obj.id)
            setIPRestrictedError(false)
            let instanceData = await LoadAllInstanceData(keys, obj?.id, dispatch, globalDispatch).catch((error) => {
              if (error?._error?.code === '403' && error?._error?.message === 'API Access is IP restricted.') {
                setIPRestrictedError(true)
              }
              instanceData = []
            })
            makeTenantSwitcherLoad(dispatch, false)
            instanceData = instanceData ? instanceData : []
            if (instanceData) {
              dispatch(loadChildInstanceData(instanceData))
            }
          }
          else {
            makeTenantSwitcherLoad(dispatch, false)
            dispatch(loadChildInstanceData([]))
          }
        }
        catch (e) {
          makeTenantSwitcherLoad(dispatch, false)
          dispatch(loadChildInstanceData([]))
        }
      }

      fetchtokens()
      setTsgAccess()
      getTsgAccess?.(obj.id).then(setTsgAccess, (err) => {
        // eslint-disable-next-line no-console
        console.error('failed to get tsg access object', err)
      })
    }
  }, [dispatch, globalDispatch, rest.instancesKeyBy])

  const onTenantSearchHandler = useCallback((tenantSearchId) => {
    if (tenantSearchId?.trim().length)
      onBreadCrumbClick(sgKeyBy[tenantSearchId])
  }, [sgKeyBy, onBreadCrumbClick])

  const onMutliTenantClick = useCallback(() => {
    globalDispatch.launchMultiTenantView(selectedTreeNode?.id, MSP_URL)
    setIsGlobalModuleOpen(false)
  }, [globalDispatch, selectedTreeNode?.id])

  const onAscClick = useMemo(() => {
    if (hasMicroApp(ASC_PORTAL_MICRO_APP) && tsgAccess?.hasRole(ASC_TIER_1_SUPPORT_ROLE)) {
      const onAscClick = () => {
        const ascBaseUrl = getFrameworkServiceUrl('asc_portal')
        if (ascBaseUrl) {
          const url = new URL(ascBaseUrl)
          if (url.origin !== window.location.origin) {
            url.searchParams.append('tsg_id', selectedTreeNode?.id)
            window.location.href = url.toString()
            return
          }
        }
        launchMultiTenantView(selectedTreeNode?.id, ASC_URL)
      }
      return onAscClick
    }
    return null
  }, [selectedTreeNode?.id, tsgAccess])

  const onHubClick = useMemo(() => {
    if (!selectedTreeNode?.id || selectedTreeNode.id === '_superRoot') {
      return null
    }
    if (getFrameworkVar('hub')) {
      return () => {
        globalDispatch.launchMultiTenantView(selectedTreeNode.id, '/hub')
        setIsGlobalModuleOpen(false)
      }
    }
    else {
      const hubUrl = getFrameworkServiceUrl('app_portal')
      if (hubUrl) {
        return () => {
          setIsGlobalModuleOpen(false)
          const url = new URL(HUB_LANDING_PATH, hubUrl)
          url.searchParams.append('tsg_id', selectedTreeNode.id)
          window.location.href = url.toString()
        }
      }
      return null
    }
  }, [globalDispatch, selectedTreeNode?.id])

  useEffect(() => {
    if (selectedTreeNode && selectedTreeNode.id !== ROOT_NODE_ID) {
      onBreadCrumbClick(selectedTreeNode)
    }
  }, [onBreadCrumbClick, selectedTreeNode])

  const ascRoleOnly = useMemo(() => tsgAccess?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE), [tsgAccess])
  const showMultitenantView = !ascRoleOnly && selectedTreeNode?.children?.length > 0 && selectedTreeNode?.id !== '_superRoot'
  const govRegion = rest.childTenantInstances.some(inst => ((inst.platform_region || inst.region) === GOV_REGION))
  const isGovSase = govRegion || (rest.childTenantInstances.length === 0 && selectedTreeNode?.isSASE)
  const { apps } = useContext(AppContext)
  const multitenantPortalName = isGovSase ? SWITCH_MULTI_TENANT_PORTAL :
    apps?.strata_cloud_manager?.extra?.multi_tenant_display_name || SWITCH_MULTI_TENANT_PORTAL

  return (
    <div className="tw-flex tw-flex-col tw-w-full tw-h-full tw-text-white tw-bg-blue-steel-700 dark:cm-bg-black tw-justify-between tw-flex-grow tenant-switcher-page">
      <TenantSearch className="tw-pt-2" onTenantsSearch={onTenantSearchHandler} treeData={sgTree} />
      <div className="tw-flex tw-flex-col tw-flex-grow tw-justify-start" style={{ height: 'calc(100% - 42px)' }}>
        <TreeComponent {...rest} globalDispatch={globalDispatch} onBreadCrumbClick={onBreadCrumbClick} isIPRestrictedError={isIPRestrictedError} />
        <div data-testid="tenant-switcher-bottom" className="tw-flex-1 tw-bg-blue-steel-900 dark:cm-bg-black">
          <div className="tw-flex tw-flex-col tw-justify-end tw-mb-4 tw-pb-2">
            {(showMultitenantView || onHubClick || onAscClick) && (<div>
              <div>
                <Body size="sm" appearance="secondary" addClassName="tw-text-gray-400 tw-py-2 tw-pl-3">
                  {LABEL_AGGREGATE_SERVICES}
                </Body>
              </div>
              {showMultitenantView && (
                <Button
                  disabled={selectedTreeNode?.id === '_superRoot'}
                  appearance='clear'
                  fullWidth
                  style={{ paddingTop: '14px', paddingBottom: '14px' }}
                  onClick={onMutliTenantClick}
                  icon={<AppImage app_id={isGovSase ? 'none' : 'strata_cloud_manager'} alt="Prisma Access" style={{ height: 'inherit', objectFit: 'contain', width: 'inherit' }} />}
                  addClassName="tw-flex tw-bg-blue-steel-900 tw-flex-row tw-justify-start tw-border-l-4 tw-border-blue-steel-900 dark:cm-border-black
                focus:tw-outline-none focus:tw-ring-offset-0 hover:tw-bg-blue-steel-700 dark:cm-bg-black hover:tw-border-blue-400 hover:tw-border-l-4
                dark:hover:tw-bg-gray-800 dark:focus:tw-bg-gray-800 dark:active:tw-bg-gray-700 focus:tw-border-l-4 focus:tw-border-blue-400"
                  size={'lg'}>
                  <Body
                    size="md"
                    appearance="primary"
                    addClassName="tw-pl-2 tw-text-white tw-color-primary tw-border-0"
                  >
                    {multitenantPortalName}
                  </Body>
                </Button>
              )}
              {onHubClick && (
                <Button
                  appearance={'ghost'}
                  fullWidth={true}
                  style={{ paddingTop: '14px', paddingBottom: '14px' }}
                  onClick={onHubClick}
                  icon={<AppImage app_id={'hub'} alt="Hub" style={{ height: 'inherit', objectFit: 'contain', width: 'inherit' }} />}
                  addClassName="tw-flex tw-bg-blue-steel-900 tw-flex-row tw-justify-start tw-border-l-4 tw-border-blue-steel-900 dark:cm-border-black
                focus:tw-outline-none focus:tw-ring-offset-0 hover:tw-bg-blue-steel-700 dark:cm-bg-black hover:tw-border-blue-400 hover:tw-border-l-4
                dark:hover:tw-bg-gray-800 dark:focus:tw-bg-gray-800 dark:active:tw-bg-gray-700 focus:tw-border-l-4 focus:tw-border-blue-400"
                  size={'lg'}>
                  <Body
                    size="md"
                    appearance="primary"
                    addClassName="tw-pl-2 tw-text-white tw-color-primary tw-border-0"
                  >
                    {HUB}
                  </Body>
                </Button>
              )}
              {onAscClick && (
                <Button
                  appearance={'ghost'}
                  fullWidth={true}
                  style={{ paddingTop: '14px', paddingBottom: '14px' }}
                  onClick={onAscClick}
                  icon={<AppImage app_id={'none'} alt="ASC" style={{ height: 'inherit', objectFit: 'contain', width: 'inherit' }} />}
                  addClassName="tw-flex tw-bg-blue-steel-900 tw-flex-row tw-justify-start tw-border-l-4 tw-border-blue-steel-900 dark:cm-border-black
                focus:tw-outline-none focus:tw-ring-offset-0 hover:tw-bg-blue-steel-700 dark:cm-bg-black hover:tw-border-blue-400 hover:tw-border-l-4
                dark:hover:tw-bg-gray-800 dark:focus:tw-bg-gray-800 dark:active:tw-bg-gray-700 focus:tw-border-l-4 focus:tw-border-blue-400"
                  size={'lg'}>
                  <Body
                    size="md"
                    appearance="primary"
                    addClassName="tw-pl-2 tw-text-white tw-color-primary tw-border-0"
                  >
                    {ASC}
                  </Body>
                </Button>
              )}
            </div>)}
            {!hideCommonServices && !ascRoleOnly && !isIPRestrictedError && <>
              <Body
                size="sm" appearance="secondary"
                style={{ width: '255px' }}
                addClassName="tw-pl-3 tw-text-gray-400 tw-text-left tw-border-0 tw-py-2 tw-color-primary"
              >
                {LABEL_COMMON_SERVICES}
              </Body>
              <CommonServices className="tw-mb-4" {...rest} globalDispatch={globalDispatch} />
            </>}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TenantSwitcherPage
